import React from 'react';
import ConfirmDeleteModal from '@/components/ui/ConfirmDeleteModal';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useUsersPage } from './UsersPage/useUsersPage';
import UsersHeader from './UsersPage/UsersHeader';
import BulkActionBar from './UsersPage/BulkActionBar';
import CreateUserForm from './UsersPage/CreateUserForm';
import UserEditForm from './UsersPage/UserEditForm';
import PasswordResetForm from './UsersPage/PasswordResetForm';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';
import UserCard from './UsersPage/UserCard';
import UserTable from './UsersPage/UserTable';

const UsersPage: React.FC = () => {
  const {
    currentUser,
    loading,
    error,
    allPlayers,
    workspaces,
    adminCount,
    showCreate,
    setShowCreate,
    createForm,
    setCreateForm,
    createLoading,
    createError,
    setCreateError,
    editingId,
    setEditingId,
    editForm,
    setEditForm,
    editLoading,
    editError,
    resetPasswordId,
    setResetPasswordId,
    newPassword,
    setNewPassword,
    resetLoading,
    resetError,
    resetSuccess,
    deleteUser,
    setDeleteUser,
    deleteLoading,
    impersonateUser,
    setImpersonateUser,
    impersonateLoading,
    handleCreate,
    startEdit,
    handleEdit,
    handleResetPassword,
    handleDelete,
    confirmImpersonate,
    startResetPassword,
    search,
    setSearch,
    filteredUsers,
    visibleUsers,
    hasMore,
    loadMore,
    selectedIds,
    allSelected,
    someSelected,
    toggleSelected,
    clearSelected,
    toggleSelectAll,
    bulkDeleteOpen,
    setBulkDeleteOpen,
    bulkDeleteLoading,
    handleBulkDelete,
  } = useUsersPage();

  const sentinelRef = useInfiniteScroll(loadMore, { enabled: hasMore });

  if (loading) {
    return (
      <DelayedLoading loading>
        <LoadingStatus variant="panel" label="Loading users" className="py-12" />
      </DelayedLoading>
    );
  }

  // Shared inline edit form
  const renderEditForm = () => (
    <UserEditForm
      editForm={editForm}
      setEditForm={setEditForm}
      editingId={editingId}
      currentUserId={currentUser?.id}
      adminCount={adminCount}
      editError={editError}
      editLoading={editLoading}
      allPlayers={allPlayers}
      workspaces={workspaces}
      onSubmit={handleEdit}
      onCancel={() => setEditingId(null)}
    />
  );

  // Shared inline password reset form
  const renderResetPasswordForm = () => (
    <PasswordResetForm
      newPassword={newPassword}
      setNewPassword={setNewPassword}
      resetError={resetError}
      resetSuccess={resetSuccess}
      resetLoading={resetLoading}
      onSubmit={handleResetPassword}
      onCancel={() => setResetPasswordId(null)}
    />
  );

  return (
    <div className="space-y-6">
      <UsersHeader
        count={filteredUsers.length}
        search={search}
        onSearchChange={setSearch}
        onAddUser={() => { setShowCreate(true); setCreateError(''); }}
      />

      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          onDelete={() => setBulkDeleteOpen(true)}
          onClear={clearSelected}
        />
      )}

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <CreateUserForm
          createForm={createForm}
          setCreateForm={setCreateForm}
          createError={createError}
          createLoading={createLoading}
          allPlayers={allPlayers}
          workspaces={workspaces}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Mobile: Card layout */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            editingId={editingId}
            resetPasswordId={resetPasswordId}
            workspaces={workspaces}
            currentUserId={currentUser?.id}
            onImpersonate={setImpersonateUser}
            onEdit={startEdit}
            onResetPassword={startResetPassword}
            onDelete={setDeleteUser}
            renderEditForm={renderEditForm}
            renderResetPasswordForm={renderResetPasswordForm}
          />
        ))}
        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
            No users found. Click "Add User" to create one.
          </div>
        )}
      </div>

      {/* Desktop: Table layout */}
      <div className="hidden md:block">
        <UserTable
          users={visibleUsers}
          totalCount={filteredUsers.length}
          editingId={editingId}
          resetPasswordId={resetPasswordId}
          workspaces={workspaces}
          currentUserId={currentUser?.id}
          selectedIds={selectedIds}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelect={toggleSelected}
          onToggleSelectAll={toggleSelectAll}
          hasMore={hasMore}
          sentinelRef={sentinelRef}
          onImpersonate={setImpersonateUser}
          onEdit={startEdit}
          onResetPassword={startResetPassword}
          onDelete={setDeleteUser}
          renderEditForm={renderEditForm}
          renderResetPasswordForm={renderResetPasswordForm}
        />
      </div>

      {/* Delete confirmation modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        onConfirm={handleDelete}
        itemName={deleteUser?.name || ''}
        loading={deleteLoading}
      />

      <ConfirmModal
        isOpen={!!impersonateUser}
        title="Impersonate user"
        tone="purple"
        confirmLabel="Impersonate"
        loading={impersonateLoading}
        message={
          <>
            You'll view and act as <strong className="text-gray-900 dark:text-gray-100">{impersonateUser?.name}</strong>{' '}
            (<span className="capitalize">{impersonateUser?.role}</span>) until you choose <em>Stop impersonating</em>.
            Everything you do will be recorded against your admin account.
          </>
        }
        onConfirm={confirmImpersonate}
        onClose={() => setImpersonateUser(null)}
      />

      <ConfirmModal
        isOpen={bulkDeleteOpen}
        title="Delete selected users"
        tone="danger"
        confirmLabel="Delete"
        loading={bulkDeleteLoading}
        message={
          <>Permanently delete <strong className="text-gray-900 dark:text-gray-100">{selectedIds.size}</strong> selected user{selectedIds.size === 1 ? '' : 's'}? This cannot be undone.</>
        }
        onConfirm={handleBulkDelete}
        onClose={() => setBulkDeleteOpen(false)}
      />
    </div>
  );
};

export default UsersPage;
